/**
 * Who is on this plan.
 *
 * Two strips in one file, because they are one section of the pane wearing two answers: over a
 * TEMPLATE the question is "which clients follow this", over a CLIENT'S PLAN it is "whose plan is
 * this" and the answer is one person. The client's strip keeps the canon's card; the template's is
 * a list, and the two still share the section's padding and its ink.
 *
 * ── The list is OPEN, and there is no disclosure (owner, 16 September 2026) ──────────────────
 * It was a stack of three overlapping discs, a sentence, and a `Pokaż listę` button that expanded a
 * table underneath. The owner asked for the clients to simply be there, one after another. Three
 * things go with that button:
 *
 * · The unreadable stack. Three 25.5px discs overlapping by five, showing two initials each, is a
 *   decoration of a list rather than a list — and nothing in it can be clicked.
 * · Two clicks to a name that always fitted on the pane anyway.
 * · `scrollIntoView` on expand. The table used to open below the fold, so the button read as a
 *   control that did nothing, and the fix was to scroll the pane after it. A list that is already
 *   open has nothing to scroll to.
 *
 * ── The box knows its height on the FIRST frame, and that is why the count matters ───────────
 * Owner decision D11: `assignedClientCount` rides on the LIST row and waits for nothing, while the
 * names come from `GET /plan-assignment` and arrive later. So `.body` reserves `min(count, VISIBLE)`
 * rows before it knows a single name, and the answer landing changes ink and never geometry. This
 * is a better position than the strip was in: the old card was a fixed height because it had to be,
 * and the list is a known height because the data says so.
 *
 * ── Above VISIBLE the rest is APPENDED, never collapsed from nothing ─────────────────────────
 * Owner, 16 September 2026: up to eight everybody stands; past eight, eight stand and a control
 * adds the remainder underneath. Deliberately not a collapse: a trainer with twenty clients would
 * otherwise be back to a disclosure, and the eight that are already drawn are the eight the screen
 * was sized for. Pressing it grows the section downward, inside the pane's own scroller.
 *
 * ── The faces have THREE states, and a failure says so in words ──────────────────────────────
 * `pending`, `unavailable` and `ready`, as one discriminated value rather than an array beside a
 * boolean: two nullable props cannot say which of the first two the pane is in, and the difference
 * is what the trainer sees. A failed assignment read leaves the heading standing and puts the
 * reason where the names would have been — inside the same reserve, so the failure swaps ink for
 * ink and moves nothing.
 *
 * **When the two disagree, the list trusts the MAP.** The count sizes the reserve and heads the
 * section; the map draws the rows. A template whose assignment rows have not caught up with its
 * counter draws fewer rows inside a reserve sized for more, which leaves a little air rather than
 * naming a client the app cannot name.
 *
 * ── What the row does NOT carry ─────────────────────────────────────────────────────────────
 * The canon's own row has `tydzień 1 z 8` and a progress bar beside the name. Owner decision **D7**
 * drops the week position everywhere, because `PlanAssignmentSummary` does not carry it, and that
 * still holds here: a plan's position for a client would be a new read or a new field, and neither
 * is licensed by "show the clients in a list".
 *
 * ── The order is the map's: surname, then full name ──────────────────────────────────────────
 * `templateFollowers` sorts with the locale's collator, and that is the order the rows keep. Now
 * that every name is visible rather than three discs and a `+N`, the order is something a trainer
 * reads rather than something nobody could see — an OPEN question is whether it should instead put
 * the clients actively training first. Not decided, so nothing here pretends it was.
 *
 * ── The faces are NOT `shared/ui/AvatarChip`, and that is deliberate ─────────────────────────
 * `AvatarChip` is the app's one 32px avatar on the roster's skin — a `--bg-sunken` ground, a
 * `--border-2` hairline, `--text-2` ink — and its own header reserves a variant for the day a third
 * diameter exists. This is not that day: this disc has a THEME-INDEPENDENT white fill, dark ink, a
 * 2px ring in the card's colour and a lift, which is four declarations rather than a size.
 * Restyling `AvatarChip` through a `className` is exactly what its header forbids.
 *
 * ── Nobody is a SENTENCE, not an empty list ──────────────────────────────────────────────────
 * A plan nobody follows draws the canon's one line, in `--text-3`, in the card it always drew it
 * in. There is nothing to list, so there is no list and no heading over one.
 */

import { useState, type CSSProperties } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { DEFAULT_ROSTER_CONTEXT, rosterContextSearch } from '~/entities/client';
import { ChevronRightIcon } from '~/shared/ui/AuthIcons';
import photoBox from '~/shared/ui/photoBox.module.css';
import skel from '~/shared/ui/skeleton.module.css';

import { personName, type PersonView } from '../model/templateFollowers';
import styles from './AssignedStrip.module.css';

/**
 * How many clients stand without being asked for. Owner, 16 September 2026.
 *
 * Eight rather than "all of them": a 37px row is a third of the pane's remaining height at the
 * 1280x620 floor, so twenty clients would push the plan's own sections off a short window. Eight is
 * the number that still fits under the muscle split there.
 */
const VISIBLE = 8;

export type StripFaces =
  | { readonly status: 'pending' }
  | { readonly status: 'unavailable' }
  | { readonly status: 'ready'; readonly people: readonly PersonView[] };

interface AssignedStripProps {
  /** From the LIST row, so the heading and the reserve never wait. */
  readonly count: number;
  readonly faces: StripFaces;
}

interface FaceProps {
  readonly person: PersonView;
  /** The client strip's 25.5px disc, or a list row's 22.5px one. */
  readonly small?: boolean;
}

function Face({ person, small }: FaceProps) {
  const size = small === true ? String(styles.faceSm) : '';

  if (person.avatarUrl === null) {
    return (
      <span className={`${String(styles.face)} ${size}`} aria-hidden>
        {person.initials}
      </span>
    );
  }

  return (
    <img
      className={`${String(photoBox.photo)} ${String(styles.face)} ${size}`}
      src={person.avatarUrl}
      alt=""
      loading="lazy"
      decoding="async"
    />
  );
}

export function AssignedStrip({ count, faces }: AssignedStripProps) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);

  if (count === 0) {
    return (
      <section className={styles.section}>
        <div className={styles.card}>
          <span className={styles.nobody}>{t('web:planLibrary.inspector.strip.nobody')}</span>
        </div>
      </section>
    );
  }

  const people = faces.status === 'ready' ? faces.people : null;
  // The reserve counts what the LIST row promised; the rows draw what the map actually carries.
  const reserved = all ? count : Math.min(count, VISIBLE);
  const shown = people === null ? null : all ? people : people.slice(0, VISIBLE);
  const remainder = count - reserved;
  const waiting = faces.status === 'pending';

  return (
    <section className={styles.section}>
      {/* A real heading, styled as the canon's eyebrow, so the pane's three sections are landmarks
        * a reader user can jump between. The count is NOT repeated here: the plate above already
        * prints it as one of its three figures, and a second copy a centimetre below would be one
        * number in two places that can disagree while a read is in flight. */}
      <h3 className={styles.eyebrow}>{t('web:planLibrary.inspector.strip.eyebrow')}</h3>

      {/* The reserve, and the only place the row count is stated — see the header. */}
      <div className={styles.body} style={{ '--strip-rows': reserved } as CSSProperties}>
        {faces.status === 'unavailable' ? (
          /* The names cannot be read at all. The reason stands where the rows would have been
           * rather than beside an empty list, and the heading above it is still true. */
          <p className={styles.note}>{t('web:planLibrary.inspector.strip.unavailable')}</p>
        ) : (
          <ul className={styles.list}>
            {shown === null
              ? // The rows that are coming, at their loaded height, holding a disc and a bar
                // instead of a person. The gate hides the bars, never the rows.
                Array.from({ length: reserved }, (_, index) => index).map((index) => (
                  // The key is PREFIXED, and that is load-bearing rather than tidy. A reserve row
                  // keyed `3` and a person row keyed `3` are the same key to React, so it reuses
                  // that one `<li>` across the swap — and the node lands wherever the SORTED list
                  // puts that client, which is a different row. Measured by
                  // `nothingOnTheScreenMovesWhileBothOfItsListsAreStillOnTheirWay`: six rows moved
                  // by one, two and four row heights on a cold entry, because six customer ids
                  // happened to collide with six indices. A prefix cannot collide with a number,
                  // so every reserve row unmounts and every person row mounts fresh.
                  <li key={`reserved-${String(index)}`} className={styles.person}>
                    <span className={styles.personBody}>
                      <i
                        className={`${String(styles.face)} ${String(styles.faceSm)} ${String(
                          styles.faceReserved,
                        )} ${waiting ? `${String(skel.bar)} ${String(skel.disc)}` : ''}`}
                        aria-hidden
                      />
                      {waiting ? (
                        <i className={`${String(skel.bar)} ${String(styles.skelName)}`} />
                      ) : null}
                    </span>
                  </li>
                ))
              : shown.map((person) => (
                  <li key={person.customerId} className={styles.person}>
                    {/* The whole row is the way to the client: a bare name beside an untouchable
                      * avatar is a target a third of the row's width, and the row has nothing else
                      * in it to press. `rosterContextSearch` is the one place that knows the
                      * default list context is never serialised. */}
                    <Link
                      className={styles.personBody}
                      to="/klienci/$clientId"
                      params={{ clientId: String(person.customerId) }}
                      search={rosterContextSearch(DEFAULT_ROSTER_CONTEXT)}
                    >
                      <Face person={person} small />
                      <span className={styles.name}>{personName(person.fullName, t)}</span>
                      <ChevronRightIcon className={styles.chevron} />
                    </Link>
                  </li>
                ))}
          </ul>
        )}
      </div>

      {remainder > 0 ? (
        <button
          type="button"
          className={styles.showAll}
          onClick={() => {
            setAll(true);
          }}
        >
          {t('web:planLibrary.inspector.strip.showAll', { count: remainder })}
        </button>
      ) : null}
    </section>
  );
}

interface ClientStripProps {
  readonly client: PersonView;
}

/**
 * Whose plan this is — the clients scope's own strip.
 *
 * One face, one name, one way to the person. It waits for nothing: the client arrived on the row the
 * trainer pointed at, which is the same read that drew the list, so this strip is complete on the
 * first frame of a swap.
 *
 * It keeps the CARD the template's strip gave up, and that is not an oversight: a card is the right
 * shape for one fact, and a one-row list would be a list pretending it might grow. The name is TEXT
 * with the link beside it, rather than the name being the link, because the plate above already
 * carries an `Edytuj` link the eye lands on first — two links a centimetre apart, one of them a bare
 * name, is the pair a trainer clicks the wrong half of.
 */
export function ClientStrip({ client }: ClientStripProps) {
  const { t } = useTranslation();

  return (
    <section className={styles.section}>
      <div className={styles.card}>
        <span className={styles.stack}>
          <Face person={client} />
        </span>
        <span className={styles.sentence}>{personName(client.fullName, t)}</span>
        <span className={styles.spacer} aria-hidden />
        <Link
          className={styles.toggle}
          to="/klienci/$clientId"
          params={{ clientId: String(client.customerId) }}
          search={rosterContextSearch(DEFAULT_ROSTER_CONTEXT)}
        >
          {t('web:planLibrary.inspector.strip.openClient')}
        </Link>
      </div>
    </section>
  );
}
